import React, { useState } from 'react';
import styles from './styles.module.scss';
import Typography from '@components/common/typography';
import Button from '@components/common/button';
import Input from '@components/common/input';
import Status from '@components/common/status';
import Table from '@components/table';
import { toast } from 'react-toastify';
import moment from 'moment/moment';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { apiAmlSettingsService, TAmlSettingsAuditItem, TAmlConnectionTestResult } from '@api';

const AUDIT_LIMIT = 20;

const AmlSettingsLayout = () => {
    // ---- settings fetch ----
    const { data: settings, handleRequest: refetchSettings, isLoading: isSettingsLoading } =
        useApiQuery({
            method: () => apiAmlSettingsService.getSettings(),
            isInitialRequest: true
        });

    // ---- connection config state ----
    const [isEditingConfig, setIsEditingConfig] = useState(false);
    const [baseUrl, setBaseUrl] = useState('');
    const [webhookUrl, setWebhookUrl] = useState('');

    const handleStartEditConfig = () => {
        setBaseUrl(settings?.base_url ?? '');
        setWebhookUrl(settings?.webhook_url ?? '');
        setIsEditingConfig(true);
    };

    const handleCancelEditConfig = () => {
        setIsEditingConfig(false);
    };

    const { handleRequest: handleSaveConfig, isLoading: isSavingConfig } = useApiMutation({
        method: () => apiAmlSettingsService.updateSettings({ base_url: baseUrl, webhook_url: webhookUrl }),
        onSuccess: () => {
            toast.success('AML connection settings updated');
            setIsEditingConfig(false);
            refetchSettings();
        },
        onError: () => toast.error('Failed to save connection settings')
    });

    // ---- API key rotation ----
    const [isRotatingApiKey, setIsRotatingApiKey] = useState(false);
    const [newApiKey, setNewApiKey] = useState('');

    const { handleRequest: handleRotateApiKey, isLoading: isRotatingApiKeyLoading } = useApiMutation({
        method: () => apiAmlSettingsService.rotateApiKey(newApiKey),
        onSuccess: () => {
            toast.success('API key rotated successfully');
            setIsRotatingApiKey(false);
            setNewApiKey('');
            refetchSettings();
        },
        onError: () => toast.error('Failed to rotate API key')
    });

    // ---- webhook secret rotation ----
    const [isRotatingSecret, setIsRotatingSecret] = useState(false);
    const [newWebhookSecret, setNewWebhookSecret] = useState('');

    const { handleRequest: handleRotateWebhookSecret, isLoading: isRotatingSecretLoading } = useApiMutation({
        method: () => apiAmlSettingsService.rotateWebhookSecret(newWebhookSecret),
        onSuccess: () => {
            toast.success('Webhook secret rotated successfully');
            setIsRotatingSecret(false);
            setNewWebhookSecret('');
            refetchSettings();
        },
        onError: () => toast.error('Failed to rotate webhook secret')
    });

    // ---- connection test ----
    const [connectionResult, setConnectionResult] = useState<TAmlConnectionTestResult | null>(null);
    const [webhookResult, setWebhookResult] = useState<TAmlConnectionTestResult | null>(null);

    const { handleRequest: handleTestConnection, isLoading: isTestingConnection } = useApiMutation({
        method: () => apiAmlSettingsService.testConnection(),
        onSuccess: (data) => setConnectionResult(data),
        onError: () => toast.error('Connection test request failed')
    });

    const { handleRequest: handleTestWebhook, isLoading: isTestingWebhook } = useApiMutation({
        method: () => apiAmlSettingsService.testWebhook(),
        onSuccess: (data) => setWebhookResult(data),
        onError: () => toast.error('Webhook test request failed')
    });

    // ---- audit log ----
    const [auditOffset, setAuditOffset] = useState(0);
    const [auditItems, setAuditItems] = useState<TAmlSettingsAuditItem[]>([]);
    const [auditTotal, setAuditTotal] = useState(0);

    const { handleRequest: refetchAudit } = useApiQuery({
        method: (req: any) =>
            apiAmlSettingsService.getAudit(AUDIT_LIMIT, req?.isNew ? 0 : auditOffset),
        isInitialRequest: true,
        onSuccess: (data, req?: any) => {
            if (req?.isNew) {
                setAuditItems(data.items);
                setAuditOffset(data.items.length);
            } else {
                setAuditItems((prev) => [...prev, ...data.items]);
                setAuditOffset((prev) => prev + data.items.length);
            }
            setAuditTotal(data.total);
        }
    });

    const auditColumns = [
        { label: 'Action', key: 'action' },
        { label: 'Field', key: 'field' },
        { label: 'Actor', key: 'actor' },
        {
            label: 'When',
            key: 'at',
            render: (value: string) =>
                value ? moment(value).format('MM.DD.YYYY HH:mm:ss') : '-'
        }
    ];

    const formatSource = (src?: string) =>
        src === 'env' ? 'from env fallback' : 'from database';

    return (
        <div className={styles.wrapper}>
            <div className={styles.header}>
                <div className={styles.text_wrapper}>
                    <Typography variant="h4">AML Settings</Typography>
                    <Typography>AventraGuard integration configuration</Typography>
                </div>
            </div>

            <div className={styles.sections}>
                {/* ---- Connection Config ---- */}
                <div className={styles.section}>
                    <div className={styles.section_header}>
                        <div className={styles.section_title}>
                            <Typography variant="h5">Connection Config</Typography>
                        </div>
                        <div className={styles.section_actions}>
                            {!isEditingConfig && (
                                <Button
                                    size="normal"
                                    variant="outlined"
                                    leftIcon="edit"
                                    disabled={isSettingsLoading}
                                    onClick={handleStartEditConfig}>
                                    Edit
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className={styles.fields_row}>
                        <div className={styles.field_block}>
                            <Input
                                full
                                label="AventraGuard base URL"
                                value={isEditingConfig ? baseUrl : (settings?.base_url ?? '')}
                                onChange={setBaseUrl}
                                disabled={!isEditingConfig}
                            />
                            <span className={styles.source_label}>
                                {settings ? formatSource(settings.source.base_url) : ''}
                            </span>
                        </div>
                        <div className={styles.field_block}>
                            <Input
                                full
                                label="Webhook URL"
                                value={isEditingConfig ? webhookUrl : (settings?.webhook_url ?? '')}
                                onChange={setWebhookUrl}
                                disabled={!isEditingConfig}
                            />
                            <span className={styles.source_label}>
                                {settings ? formatSource(settings.source.webhook_url) : ''}
                            </span>
                        </div>
                    </div>

                    {isEditingConfig && (
                        <div className={styles.section_actions} style={{ marginTop: 16 }}>
                            <Button
                                size="normal"
                                variant="outlined-dark"
                                onClick={handleCancelEditConfig}>
                                Cancel
                            </Button>
                            <Button
                                size="normal"
                                variant="primary"
                                isLoading={isSavingConfig}
                                disabled={!baseUrl || !webhookUrl}
                                onClick={() => handleSaveConfig(undefined)}>
                                Save Changes
                            </Button>
                        </div>
                    )}
                </div>

                {/* ---- AventraGuard API Key ---- */}
                <div className={styles.section}>
                    <div className={styles.section_header}>
                        <div className={styles.section_title}>
                            <Typography variant="h5">AventraGuard API Key</Typography>
                        </div>
                        <div className={styles.section_actions}>
                            {!isRotatingApiKey && (
                                <Button
                                    size="normal"
                                    variant="outlined"
                                    disabled={isSettingsLoading}
                                    onClick={() => setIsRotatingApiKey(true)}>
                                    Rotate
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className={styles.secret_row}>
                        <Status variant={settings?.api_key_set ? 'success' : 'error'}>
                            {settings?.api_key_set ? 'Configured' : 'Not set'}
                        </Status>
                        <div className={styles.secret_meta}>
                            {settings?.api_key_set && (
                                <span className={styles.masked_value}>
                                    &#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;
                                    {settings.api_key_last4}
                                </span>
                            )}
                            {settings?.api_key_rotated_at && (
                                <span className={styles.rotated_at}>
                                    Last rotated:{' '}
                                    {moment(settings.api_key_rotated_at).format(
                                        'MM.DD.YYYY HH:mm'
                                    )}
                                </span>
                            )}
                            {settings?.api_key_prev_set && (
                                <span className={styles.rotated_at}>
                                    Previous key retained for grace period
                                </span>
                            )}
                        </div>
                        <span className={styles.source_label}>
                            {settings ? formatSource(settings.source.api_key) : ''}
                        </span>
                    </div>

                    {isRotatingApiKey && (
                        <div className={styles.rotate_form}>
                            <span className={styles.rotate_warning}>
                                The current key becomes the previous (grace-period) key. Paste the
                                new key below to confirm.
                            </span>
                            <Input
                                full
                                type="password"
                                label="New API key"
                                value={newApiKey}
                                onChange={setNewApiKey}
                            />
                            <div className={styles.rotate_actions}>
                                <Button
                                    size="normal"
                                    variant="outlined-dark"
                                    onClick={() => {
                                        setIsRotatingApiKey(false);
                                        setNewApiKey('');
                                    }}>
                                    Cancel
                                </Button>
                                <Button
                                    size="normal"
                                    variant="primary"
                                    isLoading={isRotatingApiKeyLoading}
                                    disabled={!newApiKey}
                                    onClick={() => handleRotateApiKey(undefined)}>
                                    Confirm Rotate
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                {/* ---- Webhook Secret ---- */}
                <div className={styles.section}>
                    <div className={styles.section_header}>
                        <div className={styles.section_title}>
                            <Typography variant="h5">Webhook Secret</Typography>
                        </div>
                        <div className={styles.section_actions}>
                            {!isRotatingSecret && (
                                <Button
                                    size="normal"
                                    variant="outlined"
                                    disabled={isSettingsLoading}
                                    onClick={() => setIsRotatingSecret(true)}>
                                    Rotate
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className={styles.secret_row}>
                        <Status variant={settings?.webhook_secret_set ? 'success' : 'error'}>
                            {settings?.webhook_secret_set ? 'Configured' : 'Not set'}
                        </Status>
                        <div className={styles.secret_meta}>
                            {settings?.webhook_secret_set && (
                                <span className={styles.masked_value}>
                                    &#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;&#9679;
                                    {settings.webhook_secret_last4}
                                </span>
                            )}
                            {settings?.webhook_secret_rotated_at && (
                                <span className={styles.rotated_at}>
                                    Last rotated:{' '}
                                    {moment(settings.webhook_secret_rotated_at).format(
                                        'MM.DD.YYYY HH:mm'
                                    )}
                                </span>
                            )}
                            {settings?.webhook_secret_prev_set && (
                                <span className={styles.rotated_at}>
                                    Previous secret retained for grace period
                                </span>
                            )}
                        </div>
                        <span className={styles.source_label}>
                            {settings ? formatSource(settings.source.webhook_secret) : ''}
                        </span>
                    </div>

                    {isRotatingSecret && (
                        <div className={styles.rotate_form}>
                            <span className={styles.rotate_warning}>
                                The current secret becomes the previous (grace-period) secret. Paste
                                the new secret below to confirm.
                            </span>
                            <Input
                                full
                                type="password"
                                label="New webhook secret"
                                value={newWebhookSecret}
                                onChange={setNewWebhookSecret}
                            />
                            <div className={styles.rotate_actions}>
                                <Button
                                    size="normal"
                                    variant="outlined-dark"
                                    onClick={() => {
                                        setIsRotatingSecret(false);
                                        setNewWebhookSecret('');
                                    }}>
                                    Cancel
                                </Button>
                                <Button
                                    size="normal"
                                    variant="primary"
                                    isLoading={isRotatingSecretLoading}
                                    disabled={!newWebhookSecret}
                                    onClick={() => handleRotateWebhookSecret(undefined)}>
                                    Confirm Rotate
                                </Button>
                            </div>
                        </div>
                    )}
                </div>

                {/* ---- Test Actions ---- */}
                <div className={styles.section}>
                    <div className={styles.section_header}>
                        <div className={styles.section_title}>
                            <Typography variant="h5">Test Integration</Typography>
                        </div>
                    </div>

                    <div className={styles.test_buttons}>
                        <Button
                            size="normal"
                            variant="outlined"
                            isLoading={isTestingConnection}
                            onClick={() => handleTestConnection(undefined)}>
                            Test Connection
                        </Button>
                        <Button
                            size="normal"
                            variant="outlined"
                            isLoading={isTestingWebhook}
                            onClick={() => handleTestWebhook(undefined)}>
                            Send Test Webhook
                        </Button>
                    </div>

                    {connectionResult && (
                        <div
                            className={`${styles.test_result} ${connectionResult.ok ? styles.success : styles.error}`}>
                            <strong>Connection test:</strong> {connectionResult.detail}
                        </div>
                    )}
                    {webhookResult && (
                        <div
                            className={`${styles.test_result} ${webhookResult.ok ? styles.success : styles.error}`}>
                            <strong>Webhook test:</strong> {webhookResult.detail}
                        </div>
                    )}
                </div>

                {/* ---- Audit History ---- */}
                <div className={styles.section}>
                    <div className={styles.section_header}>
                        <div className={styles.section_title}>
                            <Typography variant="h5">Audit History</Typography>
                            <Typography>{auditTotal} records</Typography>
                        </div>
                    </div>

                    <Table
                        //@ts-ignore
                        columns={auditColumns}
                        rows={auditItems}
                        totalRows={auditTotal}
                        onLoadData={() => refetchAudit({ isNew: false })}
                        emptyDescription="No audit records found"
                    />
                </div>
            </div>
        </div>
    );
};

export default AmlSettingsLayout;
